import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './App.css';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// 로그인/계정 제거: 백엔드는 계정 무관(task_id 기반)이라 토큰 첨부 불필요.

const SKILL_INIT_PROMPT_PLACEHOLDER = `You are an HR skill taxonomy expert specializing in HR Tech companies.

## Company Context
HCG is South Korea's #1 Full-Stack HR Tech company (25 years history) providing:
- HR Tech Solutions: e-HR packages (hunel for enterprise, JaDE for SMB, talenx SaaS)
- AI Powered HR Services: AI Performance Management, HR Dashboard, HR Assistant, Payroll Agent, Talent Match
- Solution Operation & Maintenance: HR system support, patch, education
- Payroll Outsourcing: payroll calculation, social insurance, year-end tax settlement
- HR Consulting: performance/reward, organization design, culture, digital HR & analytics

Key roles: HR Tech developers, AI/data scientists, HR consultants, SI consultants, payroll specialists, customer success, security/infra, product managers.

## Task
For each skill provided (Korean name, English name, Korean description), generate:
1. **hard_soft**: classify as "Hard" or "Soft"
   - Hard: technical, domain-specific, measurable skills (tools, methods, knowledge)
   - Soft: behavioral, interpersonal, cognitive abilities
2. **description**: English skill description following the structure below

## Description Guidelines
- Format: "The ability to [core action] [target] using [method/tool], achieving [purpose/outcome]."
- **Base your description on the Korean description provided** — preserve the key concepts, context, and nuances
- Write in natural English, general enough to apply across roles
- Include representative tools/methods in parentheses where relevant
- Length: 1 concise sentence (100–250 characters)

## Output Format
Respond in valid JSON object:
{
  "skills": [
    {
      "skill_name": "exact English skill name as provided",
      "hard_soft": "Hard" or "Soft",
      "description": "The ability to ..."
    },
    ...
  ]
}`;


const ALL_TABS = [
  { id: 'extraction', label: 'Skill Analysis' },
  { id: 'match', label: 'Candidate Match' },
  { id: 'talent', label: 'Talent Match' },
  { id: 'search', label: 'Peer Match' },
  { id: 'team_composer', label: 'Team Composer' },
];

// 그룹별 접근 가능한 탭 (없는 그룹은 DEFAULT_ALLOWED_TABS 적용)
const GROUP_TAB_PERMISSIONS = {
  skill_hr_admin: ALL_TABS.map(t => t.id),   // 모든 탭
};
const DEFAULT_ALLOWED_TABS = ALL_TABS.map(t => t.id);  // 5개 분석 탭 전부

const computeAllowedTabs = (groups) => {
  if (!groups || groups.length === 0) return DEFAULT_ALLOWED_TABS;
  const set = new Set();
  groups.forEach(g => (GROUP_TAB_PERMISSIONS[g] || []).forEach(t => set.add(t)));
  return set.size === 0 ? DEFAULT_ALLOWED_TABS : Array.from(set);
};

// 탭 ↔ analytics 모듈(짧은 이름) 매핑
const TAB_MODULE = {
  extraction: 'SkillProfile', talent: 'EmployeeSearch', search: 'GetProfile',
  match: 'MatchJDResume', team_composer: 'TeamCompose',
};
// 프론트에 노출할 __call__ 파라미터(모듈별 화이트리스트). 나머지는 백엔드 FIXED_PARAMS 가 주입.
const EXPOSED_PARAMS = {
  SkillProfile: ['top_k', 'recommend'],
  EmployeeSearch: ['top_n', 'top_k', 'recommend'],
  MatchJDResume: ['top_k_skills'],
  TeamCompose: ['top_n', 'top_k', 'recommend'],
  GetProfile: ['top_k', 'top_similar'],
};
// 백엔드 기본값(example) 대신 프론트가 쓰는 초기값(모듈→파라미터→값).
// 예: MatchJDResume.top_k_skills 는 백엔드 기본 0(=전체 표시)이라 차트에 스킬이 다 나옴 → 5로 시작.
const PARAM_DEFAULTS = {
  MatchJDResume: { top_k_skills: 5 },
};

// 백엔드 = analytics_system L3_serving(:8001)의 /serve/*. 프론트는 refs 를 만들지 않고
// db + ver(생략 시 latest)만 보내면 :8001 이 자동 배선하고, 파라미터 화이트리스트도 서버가 처리한다.
// API_BASE: 기본 ''(상대경로 /serve/* → nginx 가 그대로 백엔드로 프록시). 직결/로컬은 REACT_APP_API_URL=http://host:8001.
const API_BASE = process.env.REACT_APP_API_URL || '';
// DB 목록 — :8001 엔 DB 목록 API 가 없어 프론트 상수로 관리(데이터 추가 시 여기만 수정).
const DB_OPTIONS = ['hr2', 'hunel'];
// 버전(ver) 목록은 GET /serve/vers?db= 로 DB별 동적 조회(아래 effect). 실패 시 이 폴백만 노출.
const VER_FALLBACK = [];

function App() {
  // 권한 계산 (로그인 제거 — 과거 admin 로그인과 동일하게 전체 탭 노출)
  const initialGroups = ['skill_hr_admin'];
  const initialAllowed = computeAllowedTabs(initialGroups);
  const initialMain = initialAllowed.slice(0, 5);   // 5개 분석 탭 전부를 메인으로
  const isAdmin = initialGroups.includes('skill_hr_admin');

  const [allowedTabs, setAllowedTabs] = useState(initialAllowed);
  const [activeTab, setActiveTab] = useState(initialAllowed[0] || 'extraction');
  // client 개념 제거. 일부 레거시 탭(skill-init/team-composer/talent-match/match) 호출의 컴파일용 상수
  // (백엔드엔 해당 라우트 없음 → 호출 시 404. 분석 3탭은 /search/* 사용으로 무관).
  const activeClient = 'default';
  const [mainTabIds, setMainTabIds] = useState(initialMain);
  const [tabOrder, setTabOrder] = useState(initialAllowed);
  const [isTabSettingsOpen, setIsTabSettingsOpen] = useState(false);
  const [draggedTabId, setDraggedTabId] = useState(null);

  // Skill Init 상태
  const [skillInitFile, setSkillInitFile] = useState(null);
  const [skillInitPrompt, setSkillInitPrompt] = useState('');
  const [skillInitResults, setSkillInitResults] = useState([]);
  const [skillInitSnapshot, setSkillInitSnapshot] = useState([]);   // 유사 그룹 매핑용 스냅샷
  const [skillInitSimPairs, setSkillInitSimPairs] = useState([]);
  const [skillInitGroups, setSkillInitGroups] = useState([]);
  const [skillInitCosine, setSkillInitCosine] = useState('0.85');
  const [skillInitClustering, setSkillInitClustering] = useState('0.90');
  const [skillInitSearch, setSkillInitSearch] = useState('');
  const [skillInitHighlightIdx, setSkillInitHighlightIdx] = useState(null);
  const [cosineOpen, setCosineOpen] = useState(true);
  const [clusterOpen, setClusterOpen] = useState(true);
  const [noticeOpen, setNoticeOpen] = useState(false);

  // Team Composer 상태
  const [teamProject, setTeamProject] = useState('');
  const [teamRoles, setTeamRoles] = useState([]);
  const [teamExpansion, setTeamExpansion] = useState(null);
  const [expandedRoleIdx, setExpandedRoleIdx] = useState(null);
  const [expandedEmpKey, setExpandedEmpKey] = useState(null);
  const [isSkillInitSaving, setIsSkillInitSaving] = useState(false);

  // Skill Analysis 상태
  const [rows, setRows] = useState([{ type: 'T', text: '', value: '' }]);
  const [topSkills, setTopSkills] = useState([]);
  const [errorValue, setErrorValue] = useState(null);

  // 백엔드(:8001 /serve)는 db + ver 로 데이터 자동 배선(refs 비노출) → 프론트는 상수 목록에서 선택만.
  const [analyticsDb, setAnalyticsDb] = useState(DB_OPTIONS[0]);   // 선택된 DB
  const [dataVer, setDataVer] = useState('');                      // 데이터 버전(연도), '' = 최신
  const [verList, setVerList] = useState(VER_FALLBACK);            // 선택된 DB의 ver 목록(/serve/vers)
  // __call__ 파라미터(/procs) 조절: 모듈별 계약 + 현재 값
  const [paramSpecs, setParamSpecs] = useState({});  // { SkillProfile:[{name,example,description}], ... }
  const [paramVals, setParamVals] = useState({});    // { SkillProfile:{top_k:10,...}, ... }
  const [settingsOpen, setSettingsOpen] = useState(false);  // 책갈피(설정) 드롭다운 열림
  
  // 실제 모델 응답을 위한 상태
  const [needsRecommend, setNeedsRecommend] = useState(false);
  const [taskResiduals, setTaskResiduals] = useState([]);
  
  // [추가] 알림 배지 전용 상태 (버튼 노출 여부와 분리)
  const [showBadge, setShowBadge] = useState(false);

  // Talent Match 상태
  const [talentRows, setTalentRows] = useState([{ text: '' }]);
  const [talentProjectSkills, setTalentProjectSkills] = useState([]);
  const [talentCandidates, setTalentCandidates] = useState([]);

  // Employee Search 상태
  const [searchName, setSearchName] = useState('');
  const [searchEmployeeSkills, setSearchEmployeeSkills] = useState([]);
  const [searchEmployeeInfo, setSearchEmployeeInfo] = useState(null);
  const [searchSimilarEmployees, setSearchSimilarEmployees] = useState([]);
  const [searchRanked, setSearchRanked] = useState([]);   // EmployeeSearch(analytics): 쿼리→직원 랭킹 [{emp,score}]

  // Resume Match 상태
  const [jdRows, setJdRows] = useState([{ text: '' }]);
  const [resumeRows, setResumeRows] = useState([{ text: '' }]);
  const [matchResults, setMatchResults] = useState([]);
  const [expandedResultIndex, setExpandedResultIndex] = useState(null);

  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // New Skills 상태 및 전용 로딩 프로그레스
  const [isNewSkillsLoading, setIsNewSkillsLoading] = useState(false);
  const [newSkillsResult, setNewSkillsResult] = useState([]);
  const [skillRecs, setSkillRecs] = useState([]);     // SkillProfile recommend=true → 신규 스킬 추천 [{skill_name,skill_description,post_sim_skills}]
  const [talentRecs, setTalentRecs] = useState([]);   // EmployeeSearch recommend
  const [teamRecs, setTeamRecs] = useState([]);       // TeamCompose recommend
  const [newSkillsProgress, setNewSkillsProgress] = useState(0);
  const newSkillsProgressRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const [editorConfig, setEditorConfig] = useState({ isOpen: false, type: '', index: null, text: '' });
  const [alertConfig, setAlertConfig] = useState({ isOpen: false, message: '', type: 'error' });

  // 로그인/클라이언트 제거: 인증 게이트·/clients 조회 없음. (탭은 초기 useState 로 이미 구성됨)

  const showAlert = (message, type = 'error') => {
    setAlertConfig({ isOpen: true, message, type });
  };

  // 탭 이동 시 분석 입력/결과 초기화(세션 없는 것처럼).
  useEffect(() => {
    setRows([{ type: 'T', text: '', value: '' }]);
    setTopSkills([]); setErrorValue(null); setNeedsRecommend(false); setSkillRecs([]);
    setSearchName(''); setSearchEmployeeInfo(null); setSearchEmployeeSkills([]); setSearchSimilarEmployees([]);
    setTalentRows([{ text: '' }]); setTalentProjectSkills([]); setTalentCandidates([]); setTalentRecs([]);
    setJdRows([{ text: '' }]); setResumeRows([{ text: '' }]); setMatchResults([]);
    setTeamProject(''); setTeamRoles([]); setTeamRecs([]); setExpandedRoleIdx(null);
    setShowBadge(false);
    setShowAnalysis(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // 탭 진입 시 그 모듈의 __call__ 파라미터 계약(/serve)만 로드해 폼 구성.
  // refs/테이블 선택은 제거(백엔드가 db+ver 로 자동 배선).
  useEffect(() => {
    const mod = TAB_MODULE[activeTab];
    if (!mod) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/serve`);   // 모듈 계약 목록(=procs.describe)
        const procsList = res.data || [];
        // 네임스페이스(hr/SkillProfile)여도 짧은 이름으로 찾기(접미사 매칭)
        const findProc = (n) => procsList.find(p => p.name === n || (p.name || '').split('/').pop() === n);
        // __call__ 파라미터: 노출 화이트리스트만 기본값으로 초기화(기존 값 보존)
        const exposed = EXPOSED_PARAMS[mod] || [];
        const ps = (findProc(mod)?.params || []).filter(p => exposed.includes(p.name));
        setParamSpecs(prev => ({ ...prev, [mod]: ps }));
        setParamVals(prev => {
          const cur = { ...(prev[mod] || {}) };
          ps.forEach(p => { if (cur[p.name] === undefined) cur[p.name] = PARAM_DEFAULTS[mod]?.[p.name] ?? p.example; });
          return { ...prev, [mod]: cur };
        });
      } catch (e) { /* backend off 등 무시 */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // 선택된 DB 의 ver(연도/시점) 목록 로드(GET /serve/vers?db=). DB 바뀌면 재조회 + 기본 선택 갱신.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/serve/vers`, { params: { db: analyticsDb } });
        if (cancelled) return;
        const vers = res.data?.vers || [];
        setVerList(vers);
        setDataVer(vers.includes('latest') ? 'latest' : (vers[0] || ''));  // 기본 = latest, 없으면 첫 항목
      } catch (e) {
        if (!cancelled) { setVerList(VER_FALLBACK); setDataVer(''); }
      }
    })();
    return () => { cancelled = true; };
  }, [analyticsDb]);

  // --- __call__ 파라미터 조절: /procs 계약을 컨트롤로 렌더, 값은 body.params 로 전송(백엔드가 타입 캐스팅) ---
  const setParam = (mod, name, value) =>
    setParamVals(prev => ({ ...prev, [mod]: { ...(prev[mod] || {}), [name]: value } }));

  // 문자열 파라미터의 설명에 'a | b' 형태가 있으면 select 옵션으로(예: method: per_desc | mean)
  const _enumOpts = (p) => {
    if (typeof p.example !== 'string') return null;
    const toks = (p.description || '').split(/[:|]/).map(s => s.trim())
      .filter(s => /^[A-Za-z_][A-Za-z0-9_]*$/.test(s));
    return toks.length >= 2 ? Array.from(new Set(toks)) : null;
  };

  const renderParams = (mod) => {
    const specs = paramSpecs[mod] || [];
    if (!specs.length) return null;
    const vals = paramVals[mod] || {};
    return (
      <div style={{ background: '#fffbea', border: '1px solid #fde047', borderRadius: '6px',
        padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.78rem' }}>
        <span style={{ fontWeight: 700, color: '#854d0e' }}>⚙️ 파라미터</span>
        {specs.map(p => {
          const isBool = typeof p.example === 'boolean';
          const opts = _enumOpts(p);
          const defVal = PARAM_DEFAULTS[mod]?.[p.name] ?? p.example;   // 프론트 오버라이드 우선
          const val = vals[p.name] ?? defVal;
          const isNum = typeof p.example === 'number';
          if (isBool) {
            return (
              <label key={p.name} title={p.description}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '10px', cursor: 'pointer', padding: '2px 0' }}>
                <span style={{ fontWeight: 600, color: val ? '#15803d' : '#475569' }}>{p.name}</span>
                <span style={{ position: 'relative', display: 'inline-block', width: '40px', height: '22px', flex: '0 0 auto' }}>
                  <input type="checkbox" checked={!!val}
                    onChange={(e) => setParam(mod, p.name, e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '22px',
                    background: val ? '#22c55e' : '#cbd5e1', transition: 'background .2s' }} />
                  <span style={{ position: 'absolute', top: '2px', left: val ? '20px' : '2px',
                    width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.35)', transition: 'left .2s' }} />
                </span>
              </label>
            );
          }
          return (
            <label key={p.name} title={`${p.name} (기본 ${String(defVal)})`}
              style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span>{p.description || p.name}</span>
              {opts ? (
                <select value={val} style={{ width: '100%' }}
                  onChange={(e) => setParam(mod, p.name, e.target.value)}>
                  {opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={isNum ? 'number' : 'text'} value={val} style={{ width: '100%' }}
                  step={isNum && !Number.isInteger(p.example) ? 'any' : 1}
                  onChange={(e) => setParam(mod, p.name, e.target.value)} />
              )}
            </label>
          );
        })}
      </div>
    );
  };

  // 설정(DB·버전·파라미터) — 모듈 실행 컨테이너에 붙는 책갈피 탭 + 드롭다운.
  // refs 는 백엔드(:8001)가 db+ver 로 자동 배선하므로 프론트는 db·ver 만 고른다.
  const renderSettingsBookmark = () => {
    const mod = TAB_MODULE[activeTab];
    if (!mod) return null;
    const hasParams = (paramSpecs[mod] || []).length > 0;
    return (
      // 컨테이너 좌측 경계 '바깥'에 붙은 세로 책갈피 + 왼쪽(바깥)으로 펼쳐지는 드롭다운
      <div style={{ position: 'absolute', top: 56, right: '100%', zIndex: 30,
        display: 'flex', alignItems: 'flex-start' }}>
        {settingsOpen && (
          <div style={{ width: 272, marginRight: '6px',
            background: '#fffdf2', border: '1px solid #fde047', borderRadius: '10px',
            boxShadow: '0 10px 26px rgba(0,0,0,0.20)', padding: '14px',
            display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* DB + 버전 선택 — 백엔드가 이 둘로 데이터를 자동 배선(refs 비노출) */}
            <div style={{ background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: '6px',
              padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.78rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontWeight: 700, color: '#155e75' }}>🗄 데이터베이스</span>
                <select value={analyticsDb} onChange={(e) => setAnalyticsDb(e.target.value)} style={{ width: '100%' }}>
                  {DB_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontWeight: 700, color: '#155e75' }}>📅 데이터 버전</span>
                <select value={dataVer} onChange={(e) => setDataVer(e.target.value)} style={{ width: '100%' }}>
                  {verList.map(v => <option key={v} value={v}>{v === 'latest' ? '최신(기본)' : v}</option>)}
                </select>
              </div>
            </div>
            {hasParams && renderParams(mod)}
          </div>
        )}
        <button onClick={() => setSettingsOpen(o => !o)}
          style={{ background: '#fde047', border: '1px solid #f0c000', borderRight: 'none',
            borderRadius: '10px 0 0 10px', padding: '16px 7px', cursor: 'pointer',
            fontWeight: 700, fontSize: '0.78rem', color: '#7c5e10', whiteSpace: 'nowrap',
            writingMode: 'vertical-rl', textOrientation: 'mixed',
            boxShadow: '-3px 3px 8px rgba(0,0,0,0.15)' }}>
          ⚙️ 데이터·파라미터 {settingsOpen ? '▶' : '◀'}
        </button>
      </div>
    );
  };

  // 신규 스킬 추천(recommendations) 렌더 — SkillProfile/EmployeeSearch/TeamCompose recommend=true 결과 공용
  const renderRecs = (recs) => {
    if (!recs || recs.length === 0) return null;
    return (
      <div className="new-skills-result-box elizax-theme" style={{ marginTop: '16px' }}>
        <h4>신규 스킬 추천 ({recs.length})</h4>
        <div className="sbase-grid">
          {recs.map((r, i) => (
            <div className="sbase-card" key={i}>
              <div className="sbase-card-header"><span className="sbase-skill-name">{r.skill_name}</span></div>
              <p className="sbase-desc">{r.skill_description}</p>
              {r.post_sim_skills && r.post_sim_skills.length > 0 && (
                <div className="sbase-nearest">
                  <span className="sbase-nearest-label">유사 기존 스킬:</span>
                  <ul>
                    {r.post_sim_skills.map((n, ni) => (
                      <li key={ni}>{n.skill} <span className="sbase-sim">({Number(n.cosine_sim).toFixed(3)})</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 탭 전환 처리 (결과는 초기화하되 알림 점만 제거, 버튼 조건은 유지)
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setShowAnalysis(false);
    setShowBadge(false); // 탭 이동 시 빨간 점 제거
    // 주의: needsRecommend는 초기화하지 않음으로써 가이드 내 Update 항목 노출 유지 여부 결정
  };

  // 가이드 토글 (알림 점만 제거)
  const toggleGuide = () => {
    if (!isGuideOpen) {
      setShowBadge(false); // 가이드 오픈 시 빨간 점 제거
    }
    setIsGuideOpen(!isGuideOpen);
  };

  // --- Talent Match 조작 ---
  const addTalentRow = () => setTalentRows([...talentRows, { text: '' }]);
  const deleteTalentRow = (index) => {
    if (talentRows.length > 1) setTalentRows(talentRows.filter((_, i) => i !== index));
    else showAlert("최소 한 개의 행은 있어야 합니다.");
  };
  const handleTalentInputChange = (index, val) => {
    const updated = [...talentRows];
    updated[index].text = val;
    setTalentRows(updated);
  };

  // --- Skill Analysis 조작 ---
  const addRow = () => setRows([...rows, { type: 'T', text: '', value: '' }]);
  const deleteRow = (index) => {
    if (rows.length > 1) setRows(rows.filter((_, i) => i !== index));
    else showAlert("최소 한 개의 행은 있어야 합니다.");
  };
  const handleInputChange = (index, field, val) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = val;
    setRows(updatedRows);
  };

  // --- Resume Match 조작 ---
  const addMatchRow = (target) => {
    if (target === 'jd') setJdRows([...jdRows, { text: '' }]);
    else setResumeRows([...resumeRows, { text: '' }]);
  };
  const deleteMatchRow = (target, index) => {
    const list = target === 'jd' ? jdRows : resumeRows;
    if (list.length > 1) {
      if (target === 'jd') setJdRows(jdRows.filter((_, i) => i !== index));
      else setResumeRows(resumeRows.filter((_, i) => i !== index));
    } else {
      showAlert("최소 한 개의 행은 있어야 합니다.");
    }
  };
  const handleMatchInputChange = (target, index, val) => {
    const updated = target === 'jd' ? [...jdRows] : [...resumeRows];
    updated[index].text = val;
    target === 'jd' ? setJdRows(updated) : setResumeRows(updated);

    if (editorConfig.isOpen && editorConfig.type === target && editorConfig.index === index) {
      setEditorConfig(prev => ({ ...prev, text: val }));
    }
  };

  // --- Skill Init: 유사 Skill 클릭 시 해당 행(UID 기반)으로 스크롤 + 강조 ---
  const scrollToSkillRowByUid = (uid) => {
    const currentIdx = skillInitResults.findIndex(r => r._uid === uid);
    if (currentIdx === -1) {
      showAlert("해당 Skill은 Result에서 제거되었습니다.");
      return;
    }
    if (skillInitSearch) setSkillInitSearch('');
    setSkillInitHighlightIdx(currentIdx);
    setTimeout(() => {
      const row = document.querySelector(`[data-row-idx="${currentIdx}"]`);
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  // 사용자 입력(클릭/키보드/휠) 발생 시 강조 해제
  useEffect(() => {
    if (skillInitHighlightIdx === null) return;
    const clear = () => setSkillInitHighlightIdx(null);
    // 클릭으로 강조가 시작된 직후 같은 클릭이 해제하지 않도록 약간 지연
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', clear);
      document.addEventListener('keydown', clear);
      document.addEventListener('wheel', clear);
    }, 250);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', clear);
      document.removeEventListener('keydown', clear);
      document.removeEventListener('wheel', clear);
    };
  }, [skillInitHighlightIdx]);

  // --- Skill Init 조작 ---
  const runSkillInit = async () => {
    if (!skillInitFile) { showAlert("Excel 파일을 업로드해주세요."); return; }

    if (skillInitResults.length > 0) {
      if (!window.confirm("현재 결과와 편집 내용이 모두 사라지고 새로 분석합니다. 계속할까요?")) return;
    }

    const cosineVal = parseFloat(skillInitCosine);
    const clusteringVal = parseFloat(skillInitClustering);
    if (isNaN(cosineVal) || isNaN(clusteringVal)) {
      showAlert("Cosine과 Clustering 임계값을 올바르게 입력해주세요."); return;
    }

    setIsLoading(true);
    setProgress(0);
    // 진행바 시뮬레이션 (실제 모듈 진행률은 알 수 없으니 점진적 증가)
    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 60) return prev + 0.45;
        if (prev < 90) return prev + 0.18;
        if (prev < 99) return prev + 0.05;
        return 99;
      });
    }, 150);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const formData = new FormData();
      formData.append('file', skillInitFile);
      formData.append('prompt', skillInitPrompt || '');
      formData.append('sim_threshold', String(cosineVal));
      formData.append('component_threshold', String(clusteringVal));

      const response = await axios.post(
        `${apiUrl}/api/${activeClient}/skill-init/run`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data && response.data.status === 'error') throw new Error(response.data.message);

      const recs = (response.data.records || []).map((r, i) => ({ ...r, _uid: `r_${Date.now()}_${i}` }));
      setSkillInitResults(recs);
      setSkillInitSnapshot(recs);   // 유사 그룹의 인덱스가 가리킬 스냅샷
      setSkillInitSimPairs(response.data.sim_pairs || []);
      setSkillInitGroups(response.data.groups || []);

    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      showAlert(`처리 중 오류: ${msg}`);
    } finally {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      setTimeout(() => setIsLoading(false), 200);
    }
  };

  const rerunSkillInit = async () => {
    if (skillInitResults.length === 0) { showAlert("재실행할 데이터가 없습니다."); return; }
    const cosineVal = parseFloat(skillInitCosine);
    const clusteringVal = parseFloat(skillInitClustering);
    if (isNaN(cosineVal) || isNaN(clusteringVal)) {
      showAlert("Cosine과 Clustering 값을 올바르게 입력해주세요."); return;
    }

    setIsLoading(true);
    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 60) return prev + 0.7;
        if (prev < 90) return prev + 0.3;
        if (prev < 99) return prev + 0.08;
        return 99;
      });
    }, 150);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const payload = {
        records: skillInitResults.filter(r => !r._deleted),
        sim_threshold: cosineVal,
        component_threshold: clusteringVal,
      };

      const response = await axios.post(
        `${apiUrl}/api/${activeClient}/skill-init/rerun`,
        payload
      );

      if (response.data && response.data.status === 'error') throw new Error(response.data.message);

      const recs = (response.data.records || []).map((r, i) => ({ ...r, _uid: `r_${Date.now()}_${i}` }));
      setSkillInitResults(recs);
      setSkillInitSnapshot(recs);   // 유사 그룹의 인덱스가 가리킬 스냅샷
      setSkillInitSimPairs(response.data.sim_pairs || []);
      setSkillInitGroups(response.data.groups || []);

    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      showAlert(`재실행 오류: ${msg}`);
    } finally {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      setTimeout(() => setIsLoading(false), 200);
    }
  };

  const handleSkillInitCellChange = (index, field, value) => {
    const updated = [...skillInitResults];
    updated[index][field] = value;
    setSkillInitResults(updated);
  };

  // 삭제 = 즉시 제거가 아니라 '삭제 예정' 토글 (soft delete)
  const deleteSkillInitRow = (index) => {
    const aliveCount = skillInitResults.filter(r => !r._deleted).length;
    const target = skillInitResults[index];
    if (!target._deleted && aliveCount <= 1) {
      showAlert("최소 한 개의 행은 남아 있어야 합니다.");
      return;
    }
    const next = [...skillInitResults];
    next[index] = { ...next[index], _deleted: !next[index]._deleted };
    setSkillInitResults(next);
  };

  // 유사 Skill 섹션에서의 복구 (uid 기준 _deleted 해제)
  const restoreSkillInitRow = (uid) => {
    setSkillInitResults(skillInitResults.map(r =>
      r._uid === uid ? { ...r, _deleted: false } : r
    ));
  };

  // --- Team Composer 실행 ---
  const runTeamComposer = async () => {
    if (!teamProject.trim()) { showAlert("Project 내용을 입력해주세요."); return; }

    setIsLoading(true);
    setShowAnalysis(false);
    setExpandedEmpKey(null);
    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 60) return prev + 0.45;
        if (prev < 90) return prev + 0.18;
        if (prev < 99) return prev + 0.05;
        return 99;
      });
    }, 150);

    try {
      // TeamCompose: 프로젝트 설명 → 역할 추출 → 역할별 직원 매칭(+recommend)
      const response = await axios.post(`${API_BASE}/serve/hr/TeamCompose`, {
        query: teamProject, params: paramVals.TeamCompose || {}, db: analyticsDb, ver: dataVer,
      });

      if (response.data?.status === 'error') throw new Error(response.data.message);

      const data = response.data?.result || {};
      setTeamRoles(data.roles || []);
      setTeamRecs(data.recommendations || []);
      setExpandedRoleIdx(0);
      setShowAnalysis(true);
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      showAlert(`Team Composer 오류: ${msg}`);
    } finally {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress(100);
      setTimeout(() => setIsLoading(false), 200);
    }
  };

  const saveSkillInitToDB = async () => {
    const finalRecords = skillInitResults.filter(r => !r._deleted);
    if (finalRecords.length === 0) { showAlert("저장할 결과가 없습니다."); return; }
    setIsSkillInitSaving(true);
    try {
      // 1. Excel 다운로드 (삭제 예정 행 / Embedding 제외)
      const wb = XLSX.utils.book_new();
      const header = ['skill_name', 'skill_name_ko', 'hard_soft', 'description'];
      const rows = finalRecords.map(r => [
        r.skill_name, r.skill_name_ko, r.hard_soft, r.description,
      ]);
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      ws['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 10 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Skill Init Results');
      XLSX.writeFile(wb, 'skill_init_results.xlsx');

      // 2. DB 반영 (삭제 예정 행 제외)
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(
        `${apiUrl}/api/${activeClient}/skill-init/save`,
        { records: finalRecords }
      );
      if (response.data && response.data.status === 'error') throw new Error(response.data.message);

      showAlert("DB에 반영되었습니다. Skill Analysis / Candidate Match를 바로 사용할 수 있습니다.", 'success');

    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      showAlert(`DB 반영 실패: ${msg}`);
    } finally {
      setIsSkillInitSaving(false);
    }
  };

  const openEditor = (type, index) => {
    if (editorConfig.isOpen && editorConfig.type === type && editorConfig.index === index) {
      setEditorConfig({ isOpen: false, type: '', index: null, text: '' });
    } else {
      let text = '';
      if (type === 'jd') text = jdRows[index].text;
      else if (type === 'resume') text = resumeRows[index].text;
      else if (type === 'prompt') text = skillInitPrompt;
      else if (type === 'skill_desc') text = skillInitResults[index]?.description || '';
      else if (type === 'team_project') text = teamProject;
      setEditorConfig({ isOpen: true, type, index, text });
    }
  };

  const handleEditorChange = (e) => {
    const newText = e.target.value;
    setEditorConfig(prev => ({ ...prev, text: newText }));
    if (editorConfig.type === 'prompt') {
      setSkillInitPrompt(newText);
    } else if (editorConfig.type === 'skill_desc') {
      handleSkillInitCellChange(editorConfig.index, 'description', newText);
    } else if (editorConfig.type === 'team_project') {
      setTeamProject(newText);
    } else {
      handleMatchInputChange(editorConfig.type, editorConfig.index, newText);
    }
  };

  // --- 엑셀 양식 다운로드 ---
  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    if (activeTab === 'extraction') {
      const ws = XLSX.utils.aoa_to_sheet([['TYPE', 'Description', 'Weight']]);
      ws['!cols'] = [{ wch: 8 }, { wch: 50 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Skill Analysis');
      XLSX.writeFile(wb, 'skill_analysis_template.xlsx');
    } else if (activeTab === 'talent') {
      const ws = XLSX.utils.aoa_to_sheet([['Task Description']]);
      ws['!cols'] = [{ wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Talent Match');
      XLSX.writeFile(wb, 'talent_match_template.xlsx');
    } else if (activeTab === 'skill_init') {
      const ws = XLSX.utils.aoa_to_sheet([['스킬명(한)', '스킬명(영)', '설명']]);
      ws['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Skill Init');
      XLSX.writeFile(wb, 'skill_init_template.xlsx');
    } else {
      const ws = XLSX.utils.aoa_to_sheet([['JD', 'Candidate']]);
      ws['!cols'] = [{ wch: 50 }, { wch: 50 }];
      XLSX.utils.book_append_sheet(wb, ws, 'Candidate Match');
      XLSX.writeFile(wb, 'candidate_match_template.xlsx');
    }
  };

  // --- 엑셀 업로드 ---
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
      const dataRows = data.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));

      if (activeTab === 'extraction') {
        const newRows = dataRows.map(row => ({
          type: (String(row[0] || 'T')).toUpperCase().startsWith('R') ? 'R' : 'T',
          text: String(row[1] || ''),
          value: row[2] !== undefined ? String(row[2]) : '',
        }));
        if (newRows.length > 0) setRows(newRows);
      } else if (activeTab === 'talent') {
        const newRows = dataRows.filter(row => row[0]).map(row => ({ text: String(row[0]) }));
        if (newRows.length > 0) setTalentRows(newRows);
      } else {
        const jds = dataRows.filter(row => row[0]).map(row => ({ text: String(row[0]) }));
        const resumes = dataRows.filter(row => row[1]).map(row => ({ text: String(row[1]) }));
        if (jds.length > 0) setJdRows(jds);
        if (resumes.length > 0) setResumeRows(resumes);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // --- 분석 실행 ---
  const runAnalysis = async () => {
    if (activeTab === 'extraction') {
      if (rows.some(row => !row.text.trim() || row.value === "")) {
        showAlert("모든 행의 내용과 가중치를 입력해주세요."); return;
      }
      if (rows.some(row => parseFloat(row.value) <= 0)) {
        showAlert(`가중치에 0보다 작은 값이 있습니다.\n0 ~ 100 사이의 값으로 입력해주세요.😊`);
        return;
      }
    } else if (activeTab === 'talent') {
      if (talentRows.some(r => !r.text.trim())) {
        showAlert("모든 행의 내용을 입력해주세요."); return;
      }
    } else if (activeTab === 'search') {
      if (!searchName.trim()) {
        showAlert("이름을 입력해주세요."); return;
      }
    } else {
      if (jdRows.some(r => !r.text.trim()) || resumeRows.some(r => !r.text.trim())) {
        showAlert("JD와 Resume의 모든 칸을 입력해주세요."); return;
      }
    }

    setIsLoading(true);
    setShowAnalysis(false);
    setExpandedResultIndex(null);
    setProgress(0);
    setNewSkillsResult([]);

    let speedFactor = activeTab === 'extraction' ? Math.max(0.1, 1.0 - (rows.length * 0.05))
      : activeTab === 'talent' ? Math.max(0.1, 1.0 - (talentRows.length * 0.05))
      : activeTab === 'search' ? 0.8
      : Math.max(0.05, 1.0 - (jdRows.length * resumeRows.length * 0.05));

    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 60) return prev + (0.65 * speedFactor);
        if (prev < 90) return prev + (0.35 * speedFactor);
        if (prev < 99) return prev + (0.1 * speedFactor);
        return 99;
      });
    }, 150);

    try {
      if (activeTab === 'extraction') {
        // SkillProfile: 멀티입력 [[text, weight, type(T/R)]] → 스킬 분포 (+recommend 체크 시 신규스킬 추천)
        const query = rows.filter(r => r.text && r.text.trim())
          .map(r => [r.text, Number(r.value) || 1, r.type]);
        const response = await axios.post(`${API_BASE}/serve/hr/SkillProfile`, {
          query, params: paramVals.SkillProfile || {}, db: analyticsDb, ver: dataVer,
        });
        if (response.data?.status === "error") throw new Error(response.data.message);
        const data = response.data?.result || {};
        setTopSkills(data.skills || []);
        setErrorValue(data.residual_error);
        setNeedsRecommend(data.needs_recommend);
        setSkillRecs(data.recommendations || []);   // recommend=true 시 신규 스킬 추천
        if (data.needs_recommend) setShowBadge(true);

      } else if (activeTab === 'talent') {
        // EmployeeSearch: 설명(들) → 적합 직원 랭킹(cosine) + 쿼리 스킬 분포
        const query = talentRows.map(r => r.text).filter(t => t && t.trim()).join("\n");
        const response = await axios.post(`${API_BASE}/serve/hr/EmployeeSearch`, {
          query, params: paramVals.EmployeeSearch || {}, db: analyticsDb, ver: dataVer,
        });
        if (response.data?.status === "error") throw new Error(response.data.message);
        const data = response.data?.result || {};
        setTalentProjectSkills(data.skills || []);
        setTalentCandidates(data.employees || []);
        setTalentRecs(data.recommendations || []);

      } else if (activeTab === 'search') {
        // GetProfile: 직원 id → 스킬 프로파일 + 유사 직원
        const response = await axios.post(`${API_BASE}/serve/hr/GetProfile`, {
          query: searchName.trim(), params: paramVals.GetProfile || {}, db: analyticsDb, ver: dataVer,
        });
        if (response.data?.status === "error") throw new Error(response.data.message);
        const data = response.data?.result || {};
        setSearchEmployeeInfo(data);                       // {employee, ...} 또는 {error, employees}
        setSearchEmployeeSkills(data.skills || []);
        setSearchSimilarEmployees(data.similar || []);

      } else {
        // MatchJDResume: 멀티입력 [[text, JD/R]] → JD×Resume 쌍별 cosine 매칭
        // 멀티입력 [text, weight, type] — MatchJDResume 는 weight 미사용(1 고정), type 으로 JD/R 구분
        const query = [
          ...jdRows.filter(r => r.text.trim()).map(r => [r.text, 1, 'JD']),
          ...resumeRows.filter(r => r.text.trim()).map(r => [r.text, 1, 'R']),
        ];
        const response = await axios.post(`${API_BASE}/serve/hr/MatchJDResume`, {
          query, params: paramVals.MatchJDResume || {}, db: analyticsDb, ver: dataVer,
        });
        if (response.data?.status === "error") throw new Error(response.data.message);
        const ar = response.data?.result?.analysis_results || [];
        const flat = [];
        ar.forEach(jd => (jd.matches || []).forEach(m => flat.push({
          jd_id: jd.jd_id,
          resume_id: m.resume_id ? m.resume_id.replace(/^res_/, 'can_') : m.resume_id,
          fit_score: (m.score * 100).toFixed(1) + "%",
          skills: m.skills || [],   // [{skill, w_resume, w_jd}] — 상세 차트가 그대로 사용
        })));
        setMatchResults(flat);
      }

      clearInterval(progressRef.current);
      setProgress(100);
      setTimeout(() => {
        setShowAnalysis(true);
        setIsLoading(false);
      }, 800);
    } catch (error) {
      clearInterval(progressRef.current);
      let finalMessage = "분석 중 오류 발생";
      if (!error.response) {
        finalMessage = "서버 연결이 안되어있습니다. 관리자에게 문의해주세요.";
      } else if (error.response.status === 504 || error.response.status === 502) {
        finalMessage = "서버 연결이 안되어있습니다. 관리자에게 문의해주세요.";
      } else {
        finalMessage = error.response?.data?.message || error.response?.data?.detail || error.message || finalMessage;
      }

      showAlert(finalMessage);
      setIsLoading(false);
      setProgress(0);
    }
  };

  const runNewSkills = async () => {
    setIsNewSkillsLoading(true);
    setNewSkillsResult([]);
    setNewSkillsProgress(0);

    newSkillsProgressRef.current = setInterval(() => {
        setNewSkillsProgress((prev) => {
          if (prev < 65) return prev + 0.8;
          if (prev < 92) return prev + 0.35;
          if (prev < 99) return prev + 0.1;
          return 99;
        });
      }, 150);
    
    try {
      // SkillRecommend: 입력 텍스트 → 가까운 신규 스킬 추천
      // (주의: :8001 SERVE 목록엔 SkillRecommend 가 없어 호출 시 404 — 백엔드에 추가되면 동작)
      const query = rows.map(row => row.text).filter(t => t && t.trim()).join("\n");
      const response = await axios.post(`${API_BASE}/serve/hr/SkillRecommend`, {
        query,
        params: paramVals.SkillRecommend || {},
        db: analyticsDb, ver: dataVer,
      });

      if (response.data && response.data.status === "error") {
        throw new Error(response.data.message);
      }

      clearInterval(newSkillsProgressRef.current);
      setNewSkillsProgress(100);

      const recommendations = response.data?.result?.recommendations || [];
      setTimeout(() => {
        const recommendedNames = recommendations.map((item, idx) =>
          `Skill ${idx + 1} : ${item.skill}`
        );
        setNewSkillsResult(recommendedNames);
        setIsNewSkillsLoading(false);
      }, 800);

    } catch (error) {
      clearInterval(newSkillsProgressRef.current);
      showAlert("추천 스킬 도출 중 오류가 발생했습니다.");
      setIsNewSkillsLoading(false);
      setNewSkillsProgress(0);
    }
  };

  const toggleDetail = (index) => {
    const targetResult = matchResults[index];
    const scoreValue = parseFloat(targetResult.fit_score.replace('%', ''));

    if (scoreValue === 0 || !targetResult.skills || targetResult.skills.length === 0) {
      showAlert("분석결과가 없습니다. 입력값을 확인해주세요.");
      return;
    }

    setExpandedResultIndex(expandedResultIndex === index ? null : index);
  };

  return (
    <div className="App">
      {tooltip.visible && (
        <div className="skill-tooltip" style={{ left: tooltip.x, top: tooltip.y - 10, transform: 'translateY(-100%)' }}>
          {tooltip.text}
        </div>
      )}
      <img src="/elizax_logo.png" alt="Elizax Logo" className="profile-img" />


      {alertConfig.isOpen && (
        <div className="modal-overlay">
          <div className="custom-alert-box">
            <div className={`alert-icon ${alertConfig.type}`}>
              {alertConfig.type === 'error' ? '!' : '✓'}
            </div>
            <p className="alert-message">{alertConfig.message}</p>
            <button className="alert-confirm-btn" onClick={() => setAlertConfig({ ...alertConfig, isOpen: false })}>
              확인
            </button>
          </div>
        </div>
      )}

      <aside className={`side-editor ${editorConfig.isOpen ? 'open' : ''}`}>
        <div className="editor-header">
          <h3>Full Content Editor</h3>
          <button className="btn-close-editor" onClick={() => setEditorConfig(prev => ({ ...prev, isOpen: false }))}>✕</button>
        </div>
        <div className="editor-subheader">
          <span className="editor-tag">
            {editorConfig.type === 'prompt' ? 'PROMPT'
              : editorConfig.type === 'skill_desc' ? `Description #${editorConfig.index + 1}`
              : `${editorConfig.type?.toUpperCase()} Item #${editorConfig.index + 1}`}
          </span>
        </div>
        <textarea
          className="editor-textarea"
          value={editorConfig.text}
          onChange={handleEditorChange}
          placeholder={editorConfig.type === 'prompt' ? SKILL_INIT_PROMPT_PLACEHOLDER : "상세 내용을 입력하세요..."}
        />
        <div className="editor-footer">
          <p>내용은 입력 리스트에 실시간으로 반영됩니다.</p>
        </div>
      </aside>

      <div className="guide-btn-container">
        <button
          className={`guide-toggle-btn ${isGuideOpen ? 'open' : ''}`}
          onClick={toggleGuide}
          title="User Guide"
        >
          {isGuideOpen ? '✕' : '💡'}
          
          {showBadge && !isGuideOpen && (
            <span className="notification-badge"></span>
          )}
        </button>
      </div>

      <aside className={`guide-memo ${isGuideOpen ? 'open' : ''}`}>
        <div className="memo-header">User Guide</div>
        <ul className="memo-content">
          {activeTab === 'team_composer' ? (
            <>
              <li>
                <strong>Project 내용</strong>
                <p>구성할 프로젝트의 내용을 자유롭게 입력하세요. 돋보기 버튼으로 큰 편집기에서 작성할 수 있습니다.</p>
              </li>
              <li>
                <strong>역할 구성</strong>
                <p>프로젝트 기반으로 도출된 역할별 카드가 표시됩니다. 각 카드는 주요 업무, 필요 역량, 추천 직원을 포함합니다.</p>
              </li>
              <li>
                <strong>추천 직원</strong>
                <p>각 역할에 가장 적합한 직원의 유사도를 확인할 수 있습니다.</p>
              </li>
              <li>
                <strong>신규 스킬 확장</strong>
                <p>프로젝트에 필요하지만 기존 스킬 베이스에 없는 스킬이 발견되면 확장 결과로 표시됩니다.</p>
              </li>
            </>
          ) : activeTab === 'skill_init' ? (
            <>
              <li>
                <strong>Excel 업로드</strong>
                <p>Skill명(한글)/Skill명(영어)/설명 컬럼이 있는 Excel을 업로드하세요. 중복 Skill이 포함되면 실행되지 않습니다.</p>
              </li>
              <li>
                <strong>Prompt</strong>
                <p>LLM에 전달할 시스템 프롬프트를 입력하세요. 비워두면 기본 프롬프트가 사용됩니다. 돋보기 버튼으로 큰 편집기를 열 수 있습니다.</p>
              </li>
              <li>
                <strong>Cosine / Clustering</strong>
                <p>Cosine은 유사 쌍 임계값, Clustering은 그룹 임계값입니다. 값을 조정하고 재실행하면 유사 Skill 결과가 갱신됩니다.</p>
              </li>
              <li>
                <strong>Run / 재실행</strong>
                <p>Run은 업로드 파일로 새 분석을 시작하고, 재실행은 편집된 결과를 사용해 임계값만 바꿔 다시 분석합니다.</p>
              </li>
              <li>
                <strong>유사 Skill</strong>
                <p>Cosine 기준 유사 쌍과 Clustering 기준 그룹이 표시됩니다. 항목을 클릭하면 Result 테이블의 해당 행으로 이동합니다.</p>
              </li>
              <li>
                <strong>Result 편집</strong>
                <p>셀을 직접 클릭해 수정할 수 있습니다. 편집 후 재실행을 권장합니다 (임베딩 정합성 유지).</p>
              </li>
              <li>
                <strong>DB 반영</strong>
                <p>최종 결과를 DB에 저장하고 Excel 파일도 함께 다운로드합니다.</p>
              </li>
            </>
          ) : activeTab === 'search' ? (
            <>
              <li>
                <strong>이름 입력</strong>
                <p>검색할 직원의 이름을 입력하세요.</p>
              </li>
              <li>
                <strong>Result</strong>
                <p>해당 직원의 스킬 분석과 유사한 스킬을 가진 직원 목록을 확인할 수 있습니다.</p>
              </li>
            </>
          ) : activeTab === 'talent' ? (
            <>
              <li>
                <strong>Task Description</strong>
                <p>추천받고자 하는 업무 내용을 상세히 기술하세요.</p>
              </li>
              <li>
                <strong>Result</strong>
                <p>입력한 업무와 가장 적합한 직원을 유사도 기반으로 추천합니다.</p>
              </li>
            </>
          ) : activeTab === 'extraction' ? (
            <>
              <li>
                <strong>Input 01. Type</strong>
                <p>T(Task) 또는 R(Resume)를 선택하여 데이터 성격을 구분하세요.</p>
              </li>
              <li>
                <strong>Input 02. Description</strong>
                <p>추출하고자 하는 업무 내용이나 역량을 상세히 기술하세요.</p>
              </li>
              <li>
                <strong>Input 03. Weight</strong>
                <p>각 항목의 중요도를 0에서 100 사이의 수치로 입력하세요.</p>
              </li>
              {/* [수정] 현재 화면에 New Skills 버튼이 노출될 때만 가이드 항목 표시 */}
              {needsRecommend && showAnalysis && (
                <li>
                  <strong>Update</strong>
                  <p>새로운 스킬이 생성되었습니다. 어떤 스킬이 생성되었는지 확인해주세요.</p>
                </li>
              )}
            </>
          ) : (
            <>
              <li>
                <strong>JD Input</strong>
                <p>채용 공고(JD) 내용을 입력하세요.</p>
              </li>
              <li>
                <strong>Resume Input</strong>
                <p>후보자의 이력서(Resume) 내용을 입력하세요.</p>
              </li>
              <li>
                <strong>Input Guide</strong>
                <p>분석결과가 안나온다면 괄호나 따옴표 등의 기호들을 삭제해주세요.</p>
              </li>
              <li>
                <strong>Match Analysis</strong>
                <p>Fit Score 옆의 버튼을 클릭해 상세 비교 차트를 확인하세요.</p>
              </li>
            </>
          )}
        </ul>
      </aside>

      <div className={`tab-menu ${allowedTabs.length <= 1 ? 'single-tab' : ''}`}>
        {tabOrder.filter(id => allowedTabs.includes(id) && mainTabIds.includes(id)).map(id => {
          const tab = ALL_TABS.find(t => t.id === id);
          if (!tab) return null;
          return (
            <button
              key={tab.id}
              className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >{tab.label}</button>
          );
        })}
        {isAdmin && (
          <div className="tab-more-wrapper"
            onMouseEnter={() => setIsMoreOpen(true)}
            onMouseLeave={() => setIsMoreOpen(false)}
          >
            <button className={`tab-item tab-more ${ALL_TABS.some(t => !mainTabIds.includes(t.id) && t.id === activeTab) ? 'active' : ''}`}>
              More <span className="tab-more-arrow">▾</span>
            </button>
            {isMoreOpen && (
              <div className="tab-more-menu">
                {tabOrder.filter(id => allowedTabs.includes(id) && !mainTabIds.includes(id)).map(id => {
                  const tab = ALL_TABS.find(t => t.id === id);
                  if (!tab) return null;
                  return (
                    <button
                      key={tab.id}
                      className={`tab-more-item ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => { handleTabChange(tab.id); setIsMoreOpen(false); }}
                    >{tab.label}</button>
                  );
                })}
                <a href="https://demo-ui.hcg-ai.com/" className="tab-more-item tab-more-external">Agent Hub ↗</a>
                {/* 형식상 배치용 Flow 세팅 (아직 기능 미연결) — 기존 '탭 설정' 자리를 대체 */}
                <button
                  className="tab-more-item tab-settings-item"
                  onClick={() => setIsMoreOpen(false)}
                >⚙ Flow</button>
              </div>
            )}
          </div>
        )}
      </div>

      {isTabSettingsOpen && (
        <div className="modal-overlay" onClick={() => setIsTabSettingsOpen(false)}>
          <div className="tab-settings-modal" onClick={e => e.stopPropagation()}>
            <div className="tab-settings-header">
              <h3>탭 설정</h3>
              <button className="btn-close-modal" onClick={() => setIsTabSettingsOpen(false)}>✕</button>
            </div>
            <p className="tab-settings-desc">드래그로 순서를 바꾸고, 체크박스로 메인 탭에 노출할지 선택하세요. 체크 해제된 항목은 More에 들어갑니다.</p>
            <ul className="tab-settings-list">
              {tabOrder.filter(id => allowedTabs.includes(id)).map(id => {
                const tab = ALL_TABS.find(t => t.id === id);
                if (!tab) return null;
                return (
                  <li
                    key={tab.id}
                    draggable
                    className={draggedTabId === tab.id ? 'dragging' : ''}
                    onDragStart={() => setDraggedTabId(tab.id)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!draggedTabId || draggedTabId === tab.id) return;
                      const newOrder = [...tabOrder];
                      const fromIdx = newOrder.indexOf(draggedTabId);
                      const toIdx = newOrder.indexOf(tab.id);
                      newOrder.splice(fromIdx, 1);
                      newOrder.splice(toIdx, 0, draggedTabId);
                      setTabOrder(newOrder);
                    }}
                    onDragEnd={() => setDraggedTabId(null)}
                  >
                    <span className="drag-handle">⋮⋮</span>
                    <label>
                      <input
                        type="checkbox"
                        checked={mainTabIds.includes(tab.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMainTabIds([...mainTabIds, tab.id]);
                          } else {
                            setMainTabIds(mainTabIds.filter(id => id !== tab.id));
                          }
                        }}
                      />
                      <span>{tab.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      <div className="container" style={{ position: 'relative' }}>
        {renderSettingsBookmark()}
        {activeTab === 'extraction' ? (
          <section>
            <div className="section-header"><h2>Description</h2></div>

            {/* 테이블/파라미터 설정 → 좌측 포스트잇(renderSettingsNotes) */}

            <div id="input-container">
              <div className="input-labels-row">
                <span className="col-type">TYPE</span>
                <span className="col-text">내용 (JOB DESCRIPTION / RESUME)</span>
                <span className="col-value">가중치 (0~100)</span>
                <span className="col-action"></span>
              </div>
              <div className="input-rows-wrapper">
                {rows.map((row, index) => (
                  <div className="input-row" key={index}>
                    <div className="col-type">
                      <select className="type-select" value={row.type} onChange={(e) => handleInputChange(index, 'type', e.target.value)}>
                        <option value="T">T</option><option value="R">R</option>
                      </select>
                    </div>
                    <div className="col-text">
                      <input type="text" className="text-input" placeholder="분석할 내용을 입력하세요" value={row.text} onChange={(e) => handleInputChange(index, 'text', e.target.value)} />
                    </div>
                    <div className="col-value">
                      <input type="number" step="1" className="num-input no-spinner" placeholder="0 ~ 100" value={row.value} onChange={(e) => handleInputChange(index, 'value', e.target.value)} />
                    </div>
                    <div className="col-action">
                      <button className="btn-delete" onClick={() => deleteRow(index)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="button-actions-row">
              <button className="btn-add-line" onClick={addRow}>Add row +</button>
              <div className="btn-group-right">
                <div className="excel-btn-wrapper"
                  onMouseEnter={(e) => e.currentTarget.classList.add('show-download')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('show-download')}
                >
                  <label className="btn-run-main btn-excel">
                    Excel Upload
                    <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} hidden />
                  </label>
                  <button className="btn-excel-download" onClick={downloadTemplate}>양식 다운로드</button>
                </div>
                <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runAnalysis} disabled={isLoading}>
                  {isLoading ? "Analyzing..." : "▶ Run Analysis"}
                </button>
              </div>
            </div>
          </section>
        ) : activeTab === 'talent' ? (
          <section>
            <div className="section-header"><h2>Task Description</h2></div>
            {/* 설정 → 좌측 포스트잇 */}
            <div id="input-container">
              <div className="input-labels-row">
                <span className="col-num-label">순번</span>
                <span className="col-text">내용(업무 설명)</span>
                <span className="col-action"></span>
              </div>
              <div className="input-rows-wrapper">
                {talentRows.map((row, index) => (
                  <div className="input-row" key={index}>
                    <div className="col-num-label">
                      <span className="row-num">{index + 1}</span>
                    </div>
                    <div className="col-text">
                      <input type="text" className="text-input" placeholder="업무 내용을 입력하세요" value={row.text} onChange={(e) => handleTalentInputChange(index, e.target.value)} />
                    </div>
                    <div className="col-action">
                      <button className="btn-delete" onClick={() => deleteTalentRow(index)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="button-actions-row">
              <button className="btn-add-line" onClick={addTalentRow}>Add row +</button>
              <div className="btn-group-right">
                <div className="excel-btn-wrapper"
                  onMouseEnter={(e) => e.currentTarget.classList.add('show-download')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('show-download')}
                >
                  <label className="btn-run-main btn-excel">
                    Excel Upload
                    <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} hidden />
                  </label>
                  <button className="btn-excel-download" onClick={downloadTemplate}>양식 다운로드</button>
                </div>
                <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runAnalysis} disabled={isLoading}>
                  {isLoading ? "Analyzing..." : "▶ Run Analysis"}
                </button>
              </div>
            </div>
          </section>
        ) : activeTab === 'search' ? (
          <section>
            {/* 설정 → 좌측 포스트잇 */}
            <div className="search-input-row">
              <span className="search-label">이름 :</span>
              <input
                type="text"
                className="search-input"
                placeholder="직원 이름을 입력하세요"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !isLoading) runAnalysis(); }}
              />
              <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runAnalysis} disabled={isLoading}>
                {isLoading ? "검색 중..." : "검색"}
              </button>
            </div>
          </section>
        ) : activeTab === 'skill_init' ? (
          <section>
            <div className="section-header"><h2>Skill Init</h2></div>
            <div className={`skill-init-notice ${noticeOpen ? 'open' : ''}`}>
              <button type="button" className="notice-header" onClick={() => setNoticeOpen(!noticeOpen)}>
                <span className="notice-toggle">{noticeOpen ? '▾' : '▸'}</span>
                <span className="notice-icon">⚠️</span>
                <span className="notice-title">
                  {noticeOpen ? '유의사항' : '시작전에 유의사항을 확인해주세요'}
                </span>
              </button>
              {noticeOpen && (
                <div className="notice-body">
                  <ol className="notice-list">
                    <li>
                      <strong>필수 입력 컬럼</strong>
                      <p>최초 입력 시 <code>스킬명(한)</code>, <code>스킬명(영)</code>, <code>설명</code> 세 컬럼은 반드시 입력해야 합니다. (<code>.xlsx</code> 양식 참고)</p>
                      <p>예외로, 재분석을 위한 최종 결과 파일(<code>skill_init_result.xlsx</code>) 재투입 시에만 정상 동작합니다.</p>
                    </li>
                    <li>
                      <strong>중복 스킬 불가</strong>
                      <p>스킬명(영) 기준으로 중복이 있을 경우 실행이 중단됩니다. 입력 전 중복 여부를 반드시 확인해주세요.</p>
                    </li>
                    <li>
                      <strong>컬럼명 변경 불가</strong>
                      <p>양식 파일의 컬럼명은 변경할 수 없습니다. 추가 컬럼이 있더라도 양식에 정의된 컬럼만 인식합니다.</p>
                    </li>
                    <li>
                      <strong>유사도 분석 기준</strong>
                      <p>유사도 점수는 스킬명이 아닌 <code>description</code> 내용을 기준으로 산출됩니다. Description의 품질이 분석 결과에 직접적인 영향을 줍니다.</p>
                    </li>
                    <li>
                      <strong>프롬프트 수정 주의</strong>
                      <p>프롬프트 내 <code>Description Guidelines</code>, <code>Output Format</code> 섹션은 분석 결과의 일관성을 위해 내용 변경을 지양해주세요.</p>
                    </li>
                  </ol>
                </div>
              )}
            </div>
            <div className="skill-init-row">
              <div className="excel-btn-wrapper skill-init-upload-wrapper"
                onMouseEnter={(e) => e.currentTarget.classList.add('show-download')}
                onMouseLeave={(e) => e.currentTarget.classList.remove('show-download')}
              >
                <label className="skill-init-upload">
                  <span className="skill-init-upload-label">
                    {skillInitFile ? `📄 ${skillInitFile.name}` : '📁 Excel 파일 업로드'}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={(e) => setSkillInitFile(e.target.files[0] || null)}
                  />
                </label>
                <button className="btn-excel-download" onClick={downloadTemplate}>양식 다운로드</button>
              </div>
              <input
                type="text"
                className="skill-init-prompt"
                placeholder={SKILL_INIT_PROMPT_PLACEHOLDER}
                value={skillInitPrompt}
                onChange={(e) => setSkillInitPrompt(e.target.value)}
              />
              <button className="btn-open-editor" title="상세 편집" onClick={() => openEditor('prompt', 0)}>🔍</button>
            </div>
            <div className="skill-init-params-row">
              <label className="threshold-label">
                Cosine
                <input type="number" step="0.01" min="0" max="1"
                  value={skillInitCosine}
                  onChange={(e) => setSkillInitCosine(e.target.value)} />
              </label>
              <label className="threshold-label">
                Clustering
                <input type="number" step="0.01" min="0" max="1"
                  value={skillInitClustering}
                  onChange={(e) => setSkillInitClustering(e.target.value)} />
              </label>
            </div>
            <div className="button-actions-row" style={{ justifyContent: 'flex-end', gap: '10px' }}>
              {skillInitResults.length > 0 && (
                <button
                  className={`btn-secondary ${isLoading ? 'loading' : ''}`}
                  onClick={rerunSkillInit}
                  disabled={isLoading}
                  title="편집된 데이터와 현재 임계값으로 다시 분석"
                >
                  {isLoading ? "재실행 중..." : "🔄 재실행"}
                </button>
              )}
              <button
                className={`btn-run-main ${isLoading ? 'loading' : ''}`}
                onClick={runSkillInit}
                disabled={isLoading}
                title="업로드한 파일과 Prompt로 새 분석 시작"
              >
                {isLoading ? "Processing..." : "▶ Run"}
              </button>
            </div>

            {skillInitResults.length > 0 && (
              <div className="skill-init-results">
                {/* 유사 Skill 영역 — 결과 테이블 위 */}
                <div className="similar-skill-section">
                  <div className="section-header">
                    <h2>유사 Skill</h2>
                  </div>

                  <div className={`similar-block ${cosineOpen ? '' : 'collapsed'}`}>
                    <button type="button" className="similar-block-header" onClick={() => setCosineOpen(!cosineOpen)}>
                      <span className="similar-block-toggle">{cosineOpen ? '▾' : '▸'}</span>
                      <span className="similar-block-title">Cosine 유사 쌍</span>
                      <span className="similar-block-count">{skillInitSimPairs.length}쌍</span>
                      <span className="similar-block-value">현재 임계값: {skillInitCosine}</span>
                    </button>
                    {cosineOpen && (skillInitSimPairs.length > 0 ? (
                      <div className="similar-groups">
                        {skillInitSimPairs.map((pair, i) => (
                          <div className="similar-group" key={i}>
                            <div className="similar-group-header">
                              Pair {i + 1}
                              <span className="similar-group-reason">— cosine {Number(pair.cosine_sim).toFixed(4)}</span>
                            </div>
                            <div className="similar-group-items">
                              {[pair.idx_a, pair.idx_b].map(memberIdx => {
                                const skill = skillInitSnapshot[memberIdx];
                                if (!skill) return null;
                                const liveRow = skillInitResults.find(r => r._uid === skill._uid);
                                const isRemoved = !liveRow || liveRow._deleted;
                                return (
                                  <div
                                    className={`similar-group-item ${isRemoved ? 'removed' : 'clickable'}`}
                                    key={memberIdx}
                                    onClick={() => !isRemoved && scrollToSkillRowByUid(skill._uid)}
                                    title={isRemoved ? "Result에서 제거된 항목" : "결과 테이블의 해당 행으로 이동"}
                                  >
                                    <span className="similar-skill-en">{skill.skill_name}</span>
                                    <span className="similar-skill-ko">{skill.skill_name_ko}</span>
                                    {isRemoved && (
                                      <button
                                        className="similar-skill-restore"
                                        onClick={(e) => { e.stopPropagation(); restoreSkillInitRow(skill._uid); }}
                                        title="이 Skill을 Result에 다시 추가합니다"
                                      >↻ 복구</button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="similar-empty">Cosine 기준 유사 쌍이 없습니다.</div>
                    ))}
                  </div>

                  <div className={`similar-block ${clusterOpen ? '' : 'collapsed'}`}>
                    <button type="button" className="similar-block-header" onClick={() => setClusterOpen(!clusterOpen)}>
                      <span className="similar-block-toggle">{clusterOpen ? '▾' : '▸'}</span>
                      <span className="similar-block-title">Clustering 그룹</span>
                      <span className="similar-block-count">{skillInitGroups.length}개</span>
                      <span className="similar-block-value">현재 임계값: {skillInitClustering}</span>
                    </button>
                    {clusterOpen && (skillInitGroups.length > 0 ? (
                      <div className="similar-groups">
                        {skillInitGroups.map((group) => (
                          <div className="similar-group" key={group.group_id}>
                            <div className="similar-group-header">
                              그룹 {group.group_id} ({group.size}개)
                              {typeof group.min_cosine === 'number' && (
                                <span className="similar-group-reason">— min cosine {Number(group.min_cosine).toFixed(4)}</span>
                              )}
                            </div>
                            <div className="similar-group-items">
                              {(group.indices || []).map(memberIdx => {
                                const skill = skillInitSnapshot[memberIdx];
                                if (!skill) return null;
                                const liveRow = skillInitResults.find(r => r._uid === skill._uid);
                                const isRemoved = !liveRow || liveRow._deleted;
                                return (
                                  <div
                                    className={`similar-group-item ${isRemoved ? 'removed' : 'clickable'}`}
                                    key={memberIdx}
                                    onClick={() => !isRemoved && scrollToSkillRowByUid(skill._uid)}
                                    title={isRemoved ? "Result에서 제거된 항목" : "결과 테이블의 해당 행으로 이동"}
                                  >
                                    <span className="similar-skill-en">{skill.skill_name}</span>
                                    <span className="similar-skill-ko">{skill.skill_name_ko}</span>
                                    {isRemoved && (
                                      <button
                                        className="similar-skill-restore"
                                        onClick={(e) => { e.stopPropagation(); restoreSkillInitRow(skill._uid); }}
                                        title="이 Skill을 Result에 다시 추가합니다"
                                      >↻ 복구</button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="similar-empty">Clustering 기준 그룹이 없습니다.</div>
                    ))}
                  </div>
                </div>

                <div className="section-header skill-init-results-header">
                  <h2>Result</h2>
                  <div className="skill-init-actions">
                    <button className={`btn-run-main ${isSkillInitSaving ? 'loading' : ''}`} onClick={saveSkillInitToDB} disabled={isSkillInitSaving}>
                      {isSkillInitSaving ? "처리 중..." : "💾 DB 반영"}
                    </button>
                  </div>
                </div>
                <div className="skill-init-toolbar">
                  <div className="skill-init-search-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      className="skill-init-search"
                      placeholder="Skill명(영어/한글)으로 검색..."
                      value={skillInitSearch}
                      onChange={(e) => setSkillInitSearch(e.target.value)}
                    />
                    {skillInitSearch && (
                      <button className="skill-init-search-clear" onClick={() => setSkillInitSearch('')}>✕</button>
                    )}
                  </div>
                  <span className="skill-init-count">
                    {(() => {
                      const q = skillInitSearch.toLowerCase().trim();
                      const visible = q
                        ? skillInitResults.filter(r => (r.skill_name || '').toLowerCase().includes(q) || (r.skill_name_ko || '').toLowerCase().includes(q)).length
                        : skillInitResults.length;
                      const pending = skillInitResults.filter(r => r._deleted).length;
                      return (
                        <>
                          {`${visible} / ${skillInitResults.length}`}
                          {pending > 0 && <span className="pending-delete-count"> · 삭제 예정 {pending}</span>}
                        </>
                      );
                    })()}
                  </span>
                </div>
                <div className="skill-init-table-wrapper">
                  <table className="result-table skill-init-table">
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ width: '22%' }}>skill_name</th>
                        <th style={{ width: '22%' }}>skill_name_ko</th>
                        <th style={{ width: '46%' }}>description</th>
                        <th style={{ width: '5%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {skillInitResults
                        .map((row, idx) => ({ row, idx }))
                        .filter(({ row }) => {
                          const q = skillInitSearch.toLowerCase().trim();
                          if (!q) return true;
                          return (row.skill_name || '').toLowerCase().includes(q)
                              || (row.skill_name_ko || '').toLowerCase().includes(q);
                        })
                        .map(({ row, idx }) => {
                      return (
                      <tr
                        key={idx}
                        data-row-idx={idx}
                        className={`${skillInitHighlightIdx === idx ? 'row-highlight' : ''} ${row._deleted ? 'row-pending-delete' : ''}`}
                      >
                        <td className="row-index-cell">{idx + 1}</td>
                        <td className="readonly-cell">
                          <span className="cell-text">{row.skill_name || ''}</span>
                        </td>
                        <td className="readonly-cell">
                          <span className="cell-text">{row.skill_name_ko || ''}</span>
                        </td>
                        <td className="desc-cell">
                          {row._deleted ? (
                            <span className="desc-text desc-disabled">{row.description}</span>
                          ) : (
                            <button
                              type="button"
                              className={`desc-preview ${editorConfig.isOpen && editorConfig.type === 'skill_desc' && editorConfig.index === idx ? 'editing' : ''}`}
                              onClick={() => openEditor('skill_desc', idx)}
                              title="클릭하여 편집"
                            >
                              {row.description
                                ? <span className="desc-text">{row.description}</span>
                                : <span className="desc-empty">설명을 입력하려면 클릭하세요</span>}
                              <span className="desc-edit-icon">✎</span>
                            </button>
                          )}
                        </td>
                        <td>
                          {row._deleted ? (
                            <button className="btn-restore-row" onClick={() => deleteSkillInitRow(idx)} title="삭제 취소">↩</button>
                          ) : (
                            <button className="btn-delete" onClick={() => deleteSkillInitRow(idx)} title="삭제 예정으로 표시">✕</button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        ) : activeTab === 'team_composer' ? (
          <section>
            <div className="section-header"><h2>Team Composer</h2></div>
            {/* 설정 → 좌측 포스트잇 */}
            <div className="team-input-row">
              <span className="team-input-label">Project 내용 :</span>
              <input
                type="text"
                className="team-input"
                placeholder="프로젝트 내용을 입력하세요"
                value={teamProject}
                onChange={(e) => setTeamProject(e.target.value)}
              />
              <button className="btn-open-editor team-editor-btn" title="상세 편집" onClick={() => openEditor('team_project', 0)}>🔍</button>
            </div>
            <div className="button-actions-row" style={{ justifyContent: 'flex-end' }}>
              <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runTeamComposer} disabled={isLoading}>
                {isLoading ? "Processing..." : "▶ Run"}
              </button>
            </div>
          </section>
        ) : (
          <section>
            {/* 설정 → 좌측 포스트잇 */}
            <div className="match-grid">
              <div className="match-col">
                <div className="section-header"><h2>JD (Job Description)</h2></div>
                <div className="match-input-box"> 
                  {jdRows.map((row, index) => (
                    <div className="input-row" key={index}>
                      <span className="row-num">{index + 1}.</span>
                      <input type="text" placeholder="요구 역량 입력" value={row.text} onChange={(e) => handleMatchInputChange('jd', index, e.target.value)} />
                      <button className="btn-open-editor" title="상세 편집" onClick={() => openEditor('jd', index)}>🔍</button>
                      <button className="btn-delete" onClick={() => deleteMatchRow('jd', index)}>✕</button>
                    </div>
                  ))}
                </div>
                <button className="btn-add-line" onClick={() => addMatchRow('jd')}>Add JD row +</button>
              </div>
              <div className="match-col">
                <div className="section-header"><h2>Candidate</h2></div>
                <div className="match-input-box">
                  {resumeRows.map((row, index) => (
                    <div className="input-row" key={index}>
                      <span className="row-num">{index + 1}.</span>
                      <input type="text" placeholder="보유 역량 입력" value={row.text} onChange={(e) => handleMatchInputChange('resume', index, e.target.value)} />
                      <button className="btn-open-editor" title="상세 편집" onClick={() => openEditor('resume', index)}>🔍</button>
                      <button className="btn-delete" onClick={() => deleteMatchRow('resume', index)}>✕</button>
                    </div>
                  ))}
                </div>
                <button className="btn-add-line" onClick={() => addMatchRow('resume')}>Add Candidate row +</button>
              </div>
            </div>
            <div className="button-actions-row" style={{ justifyContent: 'flex-end' }}>
              <div className="btn-group-right">
                <div className="excel-btn-wrapper"
                  onMouseEnter={(e) => e.currentTarget.classList.add('show-download')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('show-download')}
                >
                  <label className="btn-run-main btn-excel">
                    Excel Upload
                    <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} hidden />
                  </label>
                  <button className="btn-excel-download" onClick={downloadTemplate}>양식 다운로드</button>
                </div>
                <button className={`btn-run-main ${isLoading ? 'loading' : ''}`} onClick={runAnalysis} disabled={isLoading}>
                  {isLoading ? "Analyzing..." : "▶ Run Analysis"}
                </button>
              </div>
            </div>
          </section>
        )}

        {isLoading && (
          <div className="loading-status-container">
            <p className="loading-text">
              {progress < 30 && `데이터를 전송 중입니다... (${Math.floor(progress)}%)`}
              {progress >= 30 && progress < 80 && `AI 모델이 데이터를 분석하고 있습니다... (${Math.floor(progress)}%)`}
              {progress >= 80 && progress < 99 && `분석 결과를 생성하는 중입니다... (${Math.floor(progress)}%)`}
              {progress >= 99 && `마무리를 하고 있습니다. 잠시만 기다려 주세요! (${Math.floor(progress)}%)`}
            </p>
            <div className="loading-bar-wrapper">
              <div className="loading-bar-real-progress" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        )}

        {showAnalysis && (
          <section className="analysis-section">
            {activeTab !== 'talent' && activeTab !== 'search' && activeTab !== 'team_composer' && (
              <div className="section-header"><h2>Analysis</h2></div>
            )}
            {activeTab === 'team_composer' ? (
              <>
                {/* 역할별 카드 */}
                {teamRoles.length > 0 && (
                  <div className="team-roles-section">
                    <div className="section-header"><h2>역할 구성</h2></div>
                    {teamRoles.map((role, rIdx) => {
                      const isOpen = expandedRoleIdx === rIdx;
                      return (
                        <div className={`role-card ${isOpen ? 'open' : ''}`} key={rIdx}>
                          <button
                            type="button"
                            className="role-card-header"
                            onClick={() => setExpandedRoleIdx(isOpen ? null : rIdx)}
                          >
                            <span className="role-card-toggle">{isOpen ? '▾' : '▸'}</span>
                            <span className="role-card-title">{role.role}</span>
                            <span className="role-card-badge">{role.employees?.length || 0}명 추천</span>
                          </button>
                          {isOpen && (
                            <div className="role-card-body">
                              {/* Tasks */}
                              {role.tasks && role.tasks.length > 0 && (
                                <div className="role-tasks">
                                  <h4 className="role-section-title">주요 업무</h4>
                                  <div className="role-tasks-list">
                                    {role.tasks.map((t, ti) => (
                                      <span className="task-chip" key={ti}>{t}</span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Required Skills */}
                              {role.skills && role.skills.length > 0 && (
                                <div className="role-required-skills">
                                  <h4 className="role-section-title">필요 역량</h4>
                                  {role.skills.map((s, si) => {
                                    const pct = `${(s.weight * 100).toFixed(1)}%`;
                                    return (
                                      <div className="chart-row" key={si}>
                                        <div className="chart-label">{s.skill}</div>
                                        <div className="bar-flex-container">
                                          <div className="bar-container">
                                            <div className="bar-fill" style={{ width: pct }}></div>
                                          </div>
                                          <span className="percentage-label">{pct}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Recommended Employees */}
                              {role.employees && role.employees.length > 0 && (
                                <div className="role-recommended">
                                  <h4 className="role-section-title">추천 직원</h4>
                                  <table className="result-table role-employee-table">
                                    <thead>
                                      <tr>
                                        <th>순위</th><th>직원</th><th>유사도</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {role.employees.map((emp, ei) => {
                                        const maxSim = role.employees[0]?.score || 1;
                                        const opacity = 0.45 + 0.55 * ((emp.score || 0) / maxSim);
                                        return (
                                          <tr key={ei} style={{ opacity }}>
                                            <td className="rank-cell">
                                              <span className={`rank-badge rank-${ei + 1}`}>{ei + 1}순위</span>
                                            </td>
                                            <td>{emp.emp_id}</td>
                                            <td><span className="match-badge high">{((emp.score || 0) * 100).toFixed(1)}%</span></td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* recommend=true 시 신규 스킬 추천 */}
                {renderRecs(teamRecs)}
              </>
            ) : activeTab === 'search' ? (
              <>
                {/* GetProfile: 직원 id → 스킬 프로파일 + 유사 직원 */}
                {searchEmployeeInfo && searchEmployeeInfo.error ? (
                  <div className="no-result-container">
                    <p>직원을 찾을 수 없습니다: {searchEmployeeInfo.error}</p>
                    {searchEmployeeInfo.candidates && (
                      <p style={{ color: '#718096', fontSize: '0.85rem' }}>예시 id: {searchEmployeeInfo.candidates.join(', ')}</p>
                    )}
                  </div>
                ) : searchEmployeeSkills.length > 0 ? (
                  <>
                    <div className="section-header"><h2>{searchEmployeeInfo?.emp_id} · 스킬 프로파일</h2></div>
                    {searchEmployeeSkills.map((item, idx) => (
                      <div className="chart-row" key={idx}>
                        <div className="chart-label">{item.skill}</div>
                        <div className="bar-flex-container">
                          <div className="bar-container">
                            <div className="bar-fill" style={{ width: item.percentage || '0%' }}></div>
                          </div>
                          <span className="percentage-label">{item.percentage}</span>
                        </div>
                      </div>
                    ))}
                    {searchSimilarEmployees.length > 0 && (
                      <div className="talent-candidate-section" style={{ marginTop: '24px' }}>
                        <div className="section-header"><h2>유사 직원</h2></div>
                        <table className="result-table talent-table">
                          <thead><tr><th>순위</th><th>직원</th><th>유사도</th></tr></thead>
                          <tbody>
                            {searchSimilarEmployees.map((s, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{s.emp_id}</td>
                                <td><span className="match-badge high">{((s.score || 0) * 100).toFixed(1)}%</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : <div className="no-result-container"><p>직원 id를 입력하고 조회해보세요.</p></div>}
              </>
            ) : activeTab === 'talent' ? (
              <>
                {/* 쿼리 스킬 분포 (EmployeeSearch) */}
                {talentProjectSkills.length > 0 && (
                  <div className="talent-skill-section">
                    <div className="section-header"><h2>쿼리 스킬 분포</h2></div>
                    {talentProjectSkills.map((item, idx) => (
                      <div className="chart-row" key={idx}>
                        <div className="chart-label">{item.skill}</div>
                        <div className="bar-flex-container">
                          <div className="bar-container">
                            <div className="bar-fill" style={{ width: item.percentage || '0%' }}></div>
                          </div>
                          <span className="percentage-label">{item.percentage}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 적합 직원 랭킹 (cosine) */}
                {talentCandidates.length > 0 ? (
                  <div className="talent-candidate-section">
                    <div className="section-header"><h2>적합 직원 랭킹</h2></div>
                    <table className="result-table talent-table">
                      <thead><tr><th>순위</th><th>직원</th><th>적합도(cosine)</th></tr></thead>
                      <tbody>
                        {talentCandidates.map((res, i) => {
                          const maxSc = talentCandidates[0]?.score || 1;
                          const opacity = 0.45 + 0.55 * ((res.score || 0) / maxSc);
                          return (
                            <tr key={i} style={{ opacity }}>
                              <td>{i + 1}</td>
                              <td>{res.emp_id}</td>
                              <td><span className="match-badge high">{((res.score || 0) * 100).toFixed(1)}%</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="no-result-container"><p>분석 결과가 없습니다.</p></div>}

                {renderRecs(talentRecs)}
              </>
            ) : activeTab === 'extraction' ? (
              <>
                {topSkills.length > 0 ? (
                  topSkills.map((item, idx) => (
                    <div className="chart-row" key={idx}>
                      <div className="chart-label">{item.skill}</div>
                      <div className="bar-flex-container">
                        <div className="bar-container">
                          <div className="bar-fill" style={{ width: item.percentage || '0%' }}></div>
                        </div>
                        <span className="percentage-label">{item.percentage}</span>
                      </div>
                    </div>
                  ))
                ) : <div className="no-result-container"><p>분석 결과가 없습니다.</p></div>}
                
                {topSkills.length > 0 && paramVals.SkillProfile?.recommend && (
                  <div className="update-section">
                    <div className="section-header"><h2>Update</h2></div>

                    <div className="error-display-box">
                      <span className="error-label">Skill Alignment Gap :</span>
                      <span className="error-value">{errorValue !== null ? errorValue.toFixed(4) : "0.0000"}</span>
                    </div>

                    {skillRecs.length === 0 && (
                      <p style={{ color: '#718096', fontSize: '0.85rem', marginTop: '10px' }}>
                        추천할 신규 스킬이 없습니다. (잔차가 임계 이하이거나 추천 결과 없음)
                      </p>
                    )}

                    {renderRecs(skillRecs)}
                  </div>
                )}
              </>
            ) : (
              <div className="result-table-container">
                <table className="result-table">
                  <thead>
                    <tr>
                      <th>JD ID</th><th>Candidate ID</th><th>Fit Score</th><th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchResults.map((res, i) => (
                      <React.Fragment key={i}>
                        <tr>
                          <td>{res.jd_id}</td>
                          <td>{res.resume_id}</td>
                          <td><span className="match-badge high">{res.fit_score}</span></td>
                          <td>
                            <button className="btn-detail" onClick={() => toggleDetail(i)}>
                              {expandedResultIndex === i ? 'Close' : 'View Chart'}
                            </button>
                          </td>
                        </tr>
                        {expandedResultIndex === i && (
                          <tr>
                            <td colSpan="4" className="chart-expand-cell">
                              <div className="detail-chart-wrapper">
                                <div className="chart-close-row">
                                  <button onClick={() => setExpandedResultIndex(null)}>✕ Close Chart</button>
                                </div>
                                <div className="chart-info">
                                  <h4>Skill Match Details</h4>
                                  <p>{res.jd_id}와 {res.resume_id}의 스킬별 가중치 비교</p>
                                </div>
                                <ResponsiveContainer width="100%" height={350}>
                                  <BarChart
                                    data={res.skills}
                                    margin={{ top: 20, right: 30, left: 10, bottom: 40 }}
                                    barGap={8}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                    <XAxis
                                      dataKey="skill"
                                      tick={{ fontSize: 12, fontWeight: 600, fill: '#4A5568' }}
                                      interval={0} angle={-20} textAnchor="end" height={70} stroke="#CBD5E0"
                                    />
                                    <YAxis
                                      tick={{ fontSize: 12, fill: '#718096' }}
                                      stroke="#CBD5E0"
                                      domain={[0, 'auto']}
                                      allowDataOverflow={false}
                                    />
                                    <Tooltip cursor={{ fill: '#F7FAFC' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }} />
                                    <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '20px', fontSize: '13px', fontWeight: 600 }} />
                                    <Bar name="JD Required" dataKey="w_jd" fill="var(--elizax-blue)" radius={[4, 4, 0, 0]} barSize={24} animationDuration={1500} />
                                    <Bar name="Candidate" dataKey="w_resume" fill="var(--elizax-light-blue)" radius={[4, 4, 0, 0]} barSize={24} animationDuration={1500} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default App;